import { create } from 'zustand'

import { fetchState } from '@/api/game'
import type { PhoneState } from '@/types/game'

/**
 * Der Zustand der laufenden Runde.
 *
 * Er kommt vollständig vom Server. Es gibt bewusst keine lokale Spiellogik:
 * Die Bilanz zu berechnen hieße, die Quoten und Ergebnisse im Handy zu haben -
 * und damit vor der Aufdeckung. Genau das darf nicht passieren.
 */
interface GameStore {
    state: PhoneState | null
    loading: boolean
    error: string | null
    /** Lädt den Zustand neu - beim Start, nach Reconnect und bei Rückkehr in den Tab. */
    hydrate: () => Promise<void>
    /** Übernimmt einen Zustandswechsel aus einem Broadcast. */
    applyPhase: (payload: { phase: string; revealIndex: number; resultStage: number }) => void
    clear: () => void
}

export const useGameStore = create<GameStore>()((set, get) => ({
    state: null,
    loading: false,
    error: null,

    hydrate: async () => {
        set({ loading: true, error: null })

        try {
            set({ state: await fetchState(), loading: false })
        } catch (caught) {
            set({
                loading: false,
                error: caught instanceof Error ? caught.message : 'unknown',
            })
        }
    },

    /**
     * Der Broadcast trägt nur den Zustand, keine Spieldaten.
     *
     * Er wird sofort übernommen, damit der Bildschirm ohne Verzögerung
     * umschaltet - eine Aufdeckung, die eine Netzwerkrunde zu spät kommt,
     * wäre am Beamer sichtbar asynchron. Die zugehörigen Daten (etwa das
     * gerade aufgedeckte Ergebnis) holt der anschließende Reload nach.
     */
    applyPhase: (payload) => {
        const current = get().state

        if (current === null) {
            void get().hydrate()

            return
        }

        set({
            state: {
                ...current,
                session: {
                    ...current.session,
                    phase: payload.phase as PhoneState['session']['phase'],
                    revealIndex: payload.revealIndex,
                    resultStage: payload.resultStage,
                },
            },
        })

        // Nach dem Ende gibt es nichts mehr zu laden: Der Server hat die Daten
        // der Teilnehmenden samt Token schon gelöscht (Datenschutzerklärung).
        // Ein Nachladen endete mit 401 und risse das Handy aus der
        // Verabschiedung auf die Beitrittsseite.
        if (payload.phase === 'finished') {
            return
        }

        // Ergebnisse und eigene Bilanz kommen erst mit dem frischen Zustand -
        // sie stehen nie im Broadcast.
        void get().hydrate()
    },

    clear: () => set({ state: null, loading: false, error: null }),
}))
